import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '../hooks/useMediaQuery';
import useHistoryBack from '../hooks/useHistoryBack';
import usePresence from '../hooks/usePresence';
import useModalLayer from '../hooks/useModalLayer';
import useDragDismiss from '../hooks/useDragDismiss';

/**
 * A phone sheet: a short, read-only detail that rises from the bottom edge.
 *
 * Variant C's third overlay shape, after the drawer (content and forms) and the
 * dialog (a destructive question). Everything visual lives in
 * styles/variant-c/overlays.css — this used to be forty lines of inline style
 * with hard-coded greys that ignored the theme tokens entirely.
 *
 * `.open` goes on the wrapper, not the panel: the CSS drives the backdrop and
 * the panel from that one class, and the wrapper's `visibility` transition is
 * delayed by the panel's own duration so the close animates as well as the open.
 */
const BottomDrawer = ({ isOpen, onClose, title, children }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const titleId = useId();
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    // Handle system back button on mobile
    useHistoryBack(isOpen && isMobile, onClose, true, 'bottomDrawer');

    const [isMounted, isAnimating] = usePresence(isOpen, 300);
    useModalLayer(isOpen && isMounted, onClose, wrapRef, panelRef);
    // the handle it draws is a promise: pull the sheet down to close it
    const onGrab = useDragDismiss(panelRef, onClose);

    useEffect(() => {
        if (isMounted) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMounted]);

    if (typeof document === 'undefined' || !isMounted) {
        return null;
    }

    return createPortal(
        <div ref={wrapRef} className={`sheet-wrap ${isAnimating ? 'open' : ''}`}>
            <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
            <div ref={panelRef} className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} onPointerDown={(e) => { if (!e.target.closest('.sheet-body')) onGrab(e); }}>
                <div className="sheet-head">
                    <h3 id={titleId}>{title}</h3>
                    <button className="icon-btn" onClick={onClose} aria-label={t('general.close')}>
                        <X size={18} />
                    </button>
                </div>
                <div className="sheet-body">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default BottomDrawer;
